import {CommonModule} from '@angular/common';
import {Component, EventEmitter, inject, Input, Output, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {map, startWith} from 'rxjs/operators';

import {InstitutionService} from 'libs/data-access/institution/src/lib/services/institution.service';
import {InstitutionContextService} from 'libs/data-access/institution/src/lib/services/institution-context.service';
import {PicmonicService} from 'libs/data-access/picmonic/src/lib/services/picmonic.service';
import {HasPermissionDirective} from 'libs/shared/faculty/src/lib/directives/has-permission.directive';
import {FacultyAppContextService} from 'libs/shared/faculty/src/lib/service/faculty-app-context.service';
import {FeatureFlagsEnum} from 'libs/shared/feature-flag/src/lib/enums/feature-flags.enum';
import {FeatureFlagService} from 'libs/shared/feature-flag/src/lib/services/feature-flag/feature-flag.service';
import {PermissionCodesEnum} from 'libs/shared/permission/src/lib/enums/permissions.enum';
import {ApexNavigationService} from 'libs/shared/service/src/lib/services/apex-navigation/apex-navigation.service';
import {NavItemComponent} from 'libs/shared/ui/src/lib/components/nav-bar/nav-item/nav-item.component';
import {NavLogoComponent} from 'libs/shared/ui/src/lib/components/nav-bar/nav-logo/nav-logo.component';
import {NavigationItemsEnum} from '../../enums/navigation-items.enum';

@Component({
    imports: [CommonModule, HasPermissionDirective, RouterLink, RouterLinkActive, NavItemComponent, NavLogoComponent],
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-nav-list',
    templateUrl: './navigation-list.component.html'
})
export class NavigationListComponent {
    private institutionContextService = inject(InstitutionContextService);
    private institutionService = inject(InstitutionService);
    private apexNavigationService = inject(ApexNavigationService);
    private facultyAppContextService = inject(FacultyAppContextService);
    private picmonicService = inject(PicmonicService);
    private featureFlag = inject(FeatureFlagService);

    navItems = NavigationItemsEnum;
    @Output() navItemClicked = new EventEmitter<NavigationItemsEnum>();
    @Input() hideNavLinks = false;

    permissions = PermissionCodesEnum;
    assessmentsLinkPermissions = [this.permissions.ManageAssessments, this.permissions.ViewAssessments];
    questionsLinkPermissions = [this.permissions.AccessExam];
    quizzesLinkPermissions = [
        this.permissions.AccessExam,
        this.permissions.AssignQuizzes,
        this.permissions.CreateQuizzes,
        this.permissions.ViewAllQuizFolders,
        this.permissions.ViewQuizResults
    ];
    performanceInsightsPermissions = [
        this.permissions.ViewInstitutionPerformance,
        this.permissions.ViewIndividualPerformance
    ];
    expanded = signal(true);
    aiToolsSubmenuExpanded = signal(false);

    hasValidExamBanks$ = this.institutionService.hasValidExamBanks$;

    canAccessCurriculums$ = this.facultyAppContextService.contextAccountInformation$.pipe(
        map((accountInfo) => {
            return (
                accountInfo.Account?.IsInstitutionAdmin ||
                accountInfo.Account?.Permissions?.some((x) => x.Code === PermissionCodesEnum.ManageCurriculums) ||
                accountInfo.Account?.AssignedToCurriculum
            );
        }),
        startWith(false)
    );

    isApexInstitution$ = this.institutionContextService.contextInstitutionInformation$.pipe(
        map((institution) => institution.IsApexInstitution),
        startWith(false)
    );
    isMedalityInstitution$ = this.institutionContextService.contextInstitutionInformation$.pipe(
        map((institution) => this.medalityImageViewerFlag && institution.IsMedalityInstitution),
        startWith(false)
    );
    isApex$ = this.apexNavigationService.isApex$;

    learnersTab$ = this.institutionContextService.contextLearnerText$.pipe(map((text) => text.PluralForm));

    picmonicDashboardUrl = toSignal(
        this.picmonicService
            .getPicmonicNavigationLinks()
            .pipe(map((response) => response?.EducatorDashboardUrl ?? response?.StudentDashboardUrl ?? null)),
        {initialValue: null}
    );

    readonly medalityImageViewerFlag = this.featureFlag.getFeatureFlag<boolean>(FeatureFlagsEnum.MEDALITY_IMAGE_VIEWER);
    readonly contentLibraryFlagEnabled = this.featureFlag.getFeatureFlag<boolean>(
        FeatureFlagsEnum.FACULTY_CONTENT_LIBRARY
    );
    readonly aiFacultyFeaturesFlagEnabled = this.featureFlag.getFeatureFlag<boolean>(
        FeatureFlagsEnum.AI_REPORT_BOT_FACULTY
    );

    onNavItemClicked(navItem: NavigationItemsEnum): void {
        this.navItemClicked.emit(navItem);
        if (navItem === NavigationItemsEnum.Collapse) {
            this.expanded.set(!this.expanded());
        }
    }

    onApexCoursesClick(): void {
        this.apexNavigationService.navigateToApex();
    }

    onPicmonicLinkClick(): void {
        window.open(this.picmonicDashboardUrl(), '_self');
    }

    toggleAiToolsSubmenu(): void {
        this.aiToolsSubmenuExpanded.set(!this.aiToolsSubmenuExpanded());
    }
}
